// Phase 78ah — Drag-to-reorder extra stops list.
//
// Renders ONE side's worth of extra stops (pickups OR drops) inside a
// dnd-kit SortableContext. Used by both Booking trips and Admin Trip
// Monitoring so the visual + behaviour stays identical. Already-reached
// stops are rendered non-draggable so the pilot's progress timeline
// can't be reshuffled.
//
// Props:
//   tripId          – string
//   side            – 'pickup' | 'drop'
//   stops           – array of stop objects (already sorted)
//   canEdit         – bool, gates drag handle + delete button
//   labelPrefix     – 'PICKUP STOP' | 'DROP STOP'
//   colorHex        – '#00C853' (pickup) or '#FF3333' (drop)
//   onChange        – callback after a successful reorder / delete
//   onDelete        – (stopId) => Promise<void>   custom delete fn
//   renderExtras    – optional (stop)=>JSX hook for child-specific extras (e.g. pilot's Reached/Done buttons)
//
import React, { useState } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVertical } from '@phosphor-icons/react';
import api from '../lib/api';

function fmtClockIST(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
    });
  } catch { return ''; }
}

function SortableRow({ stop, idx, side, canEdit, labelPrefix, colorHex, onDelete, renderExtras }) {
  const locked = !!stop.reached_at;
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: stop.id, disabled: locked || !canEdit });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg bg-white border px-2.5 py-2 text-[10px] italic underline underline-offset-2 space-y-1 text-[#0A0A0A] ${isDragging ? 'shadow-lg ring-2' : ''}`}
      // colour border per side
      // eslint-disable-next-line react/forbid-dom-props
      data-testid={`extra-${side}-row-${idx}`}
    >
      <div className="flex items-center justify-between gap-2 not-italic no-underline" style={{ borderColor: colorHex }}>
        <div className="flex items-center gap-1.5 min-w-0">
          {canEdit && !locked && (
            <button
              type="button"
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-[#666] hover:text-[#0A0A0A] touch-none"
              title="Drag to reorder"
              data-testid={`drag-handle-${side}-${idx}`}
              aria-label="Drag to reorder"
            >
              <DotsSixVertical size={14} weight="bold" />
            </button>
          )}
          <span className="font-mono font-bold" style={{ color: colorHex }}>
            {labelPrefix} {idx + 2}
          </span>
          {locked && (
            <span className="text-[8px] uppercase tracking-widest text-[#666] bg-[#F3F3F3] rounded-full px-1.5 py-0.5">locked</span>
          )}
        </div>
        {!locked && canEdit && (
          <button
            onClick={() => onDelete(stop.id)}
            className="text-[#FF3333] text-[9px] uppercase tracking-widest hover:underline"
            data-testid={`delete-extra-${side}-${idx}`}
          >Delete</button>
        )}
      </div>
      <p className="font-bold">{stop.supplier_name}</p>
      {(stop.item || stop.qty) && <p>{stop.item}{stop.qty ? ` · ${stop.qty} ${stop.uom || ''}` : ''}</p>}
      {stop.remarks && <p className="text-[#B27300]">REMARK · {stop.remarks}</p>}
      <p className="not-italic no-underline text-[#666]">
        {stop.added_by_name && <>Added by {stop.added_by_name} </>}
        {stop.reached_at && <span className="text-[#00C853] font-mono ml-2">✓ reached {fmtClockIST(stop.reached_at)}</span>}
        {stop.done_at && <span className="text-[#00C853] font-mono ml-2">✓ done {fmtClockIST(stop.done_at)}</span>}
      </p>
      {renderExtras && renderExtras(stop)}
    </div>
  );
}

export default function SortableExtraStops({
  tripId, side, stops, canEdit, labelPrefix, colorHex, onChange, onDelete, renderExtras,
}) {
  const [local, setLocal] = useState(stops);
  // Keep local in sync when parent reloads.
  React.useEffect(() => { setLocal(stops); }, [stops]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = local.findIndex(s => s.id === active.id);
    const newIdx = local.findIndex(s => s.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    // Don't allow moving across the locked block.
    if (local[newIdx]?.reached_at) {
      alert('Cannot move into a position already reached by the pilot.');
      return;
    }
    const reordered = arrayMove(local, oldIdx, newIdx);
    setLocal(reordered); // optimistic
    try {
      await api.put(`/trips/${tripId}/extra-stops/reorder`, {
        side,
        stop_ids: reordered.map(s => s.id),
      });
      if (onChange) onChange();
    } catch (err) {
      // Roll back on failure.
      setLocal(local);
      alert(err.response?.data?.detail || 'Reorder failed');
    }
  };

  if (!local || local.length === 0) return null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={local.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2" data-testid={`sortable-${side}-list`}>
          {local.map((stop, idx) => (
            <SortableRow
              key={stop.id}
              stop={stop}
              idx={idx}
              side={side}
              canEdit={canEdit}
              labelPrefix={labelPrefix}
              colorHex={colorHex}
              onDelete={onDelete}
              renderExtras={renderExtras}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
